// src/pages/SuperAdminUsers_Edit.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  ArrowLeft,
  UserCog,
  Mail,
  Shield,
  ToggleLeft,
  ToggleRight,
  Save,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com/";
const ROLES = ["SUPERADMIN", "AUDITOR"];

export default function SuperAdminUsers_Edit() {
  const nav = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);

  const [display_name, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("SUPERADMIN");
  const [is_active, setIsActive] = useState(1);

  // reset password panel
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  // NEW: reveal toggles for password inputs
  const [revealNew, setRevealNew] = useState(false);
  const [revealConfirm, setRevealConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/superadmin/users/${id}`);
      const u = r.data?.user;
      setUser(u);
      setDisplayName(u?.display_name || "");
      setEmail(u?.email || "");
      setRole(u?.role || "SUPERADMIN");
      setIsActive(u?.is_active ? 1 : 0);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e.response?.data?.error || "Failed to load user", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [id]);

  const save = async () => {
    if (email && !/^\S+@\S+\.\S+$/.test(email))
      return Swal.fire("Validation", "Invalid email", "warning");
    if (!ROLES.includes(role))
      return Swal.fire("Validation", "Invalid role", "warning");

    try {
      setSaving(true);
      Swal.fire({
        title: "Saving...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      await axios.put(`${API}/api/superadmin/users/${id}`, {
        display_name: display_name?.trim() || null,
        email: email?.trim() || null,
        role,
        is_active: Number(is_active) ? 1 : 0,
      });
      Swal.close();
      Swal.fire({ icon: "success", title: "Saved" });
      nav("/superAdmin/superAdminUsers_List");
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.error || e.response?.data?.debug || "Save failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const setPassword = async () => {
    if (!pwd) return Swal.fire("Validation", "Password is required", "warning");
    if (pwd.length < 8)
      return Swal.fire("Validation", "Password must be at least 8 characters", "warning");
    if (pwd !== pwd2)
      return Swal.fire("Validation", "Passwords do not match", "warning");

    try {
      setPwdSaving(true);
      Swal.fire({
        title: "Updating password...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      await axios.put(`${API}/api/superadmin/users/${id}/password`, { password: pwd });
      Swal.close();
      setPwd("");
      setPwd2("");
      Swal.fire({ icon: "success", title: "Password updated" });
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data?.error || e.response?.data?.debug || "Password update failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setPwdSaving(false);
    }
  };

  // Loading / not-found states styled
  if (loading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(1100px_500px_at_50%_-120px,rgba(15,23,42,0.10),transparent)] bg-slate-100">
        <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
          <SuperAdminNav />
          <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
            <div className="px-3 sm:px-4 lg:px-6 pt-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                    <UserCog className="h-5 w-5" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Edit Super Admin</h1>
                    <p className="text-sm text-slate-600">Loading…</p>
                  </div>
                </div>
                <div className="rounded-xl bg-white px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-200">
                  <Time />
                </div>
              </div>
            </div>
            <div className="px-3 sm:px-4 lg:px-6 py-6">
              <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-6 shadow-xl backdrop-blur">
                <p className="text-slate-600">Please wait…</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100">
        <SuperAdminNav />
        <main className="p-6">
          <div className="rounded-xl border bg-white p-6 shadow">
            <p>User not found.</p>
            <p className="mt-2">
              <Link to="/superadmin/users" className="text-blue-600 hover:underline">
                Back to list
              </Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  const statusPill =
    Number(is_active) === 1
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
      : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

  return (
    <div className="min-h-screen bg-[radial-gradient(1100px_500px_at_50%_-120px,rgba(15,23,42,0.10),transparent)] bg-slate-100">
      {/* 25/75 layout */}
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          {/* Header pill bar */}
          <div className="px-3 sm:px-4 lg:px-6 pt-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                  <UserCog className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">
                    Edit Super Admin — <span className="text-slate-700">{user.username}</span>
                  </h1>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs ${statusPill}`}
                    >
                      {Number(is_active) === 1 ? (
                        <span className="inline-flex items-center gap-1">
                          <ToggleRight className="h-4 w-4" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <ToggleLeft className="h-4 w-4" /> Inactive
                        </span>
                      )}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
                      <Shield className="h-4 w-4 text-indigo-600" /> {role}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link
                  to="/superAdmin/superAdminUsers_List"
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to list
                </Link>
                <div className="rounded-xl bg-white px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-200">
                  <Time />
                </div>
              </div>
            </div>
          </div>

          {/* Content grid */}
          <div className="px-3 sm:px-4 lg:px-6 py-6">
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
              {/* Left: User Details */}
              <div className="col-span-12 xl:col-span-7 rounded-2xl border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur">
                <div className="flex items-center gap-2 border-b border-slate-200/70 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 rounded-t-2xl">
                  <UserCog className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold text-slate-800">User Details</h3>
                </div>

                <div className="p-4 lg:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Username (read-only) */}
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Username
                    </label>
                    <input
                      value={user.username}
                      disabled
                      className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-slate-900 shadow-sm"
                    />
                  </div>

                  {/* Display Name */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Display Name
                    </label>
                    <input
                      value={display_name}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Display name"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Role</label>
                    <div className="relative">
                      <Shield className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 shadow-sm focus:outline-none"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Status
                    </label>
                    <select
                      value={is_active}
                      onChange={(e) => setIsActive(Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:outline-none"
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="border-t border-slate-200/70 px-4 lg:px-6 py-4 flex items-center justify-end gap-2 rounded-b-2xl bg-white/60">
                  
                  <button
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-black focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>

              {/* Right: Reset Password */}
              <div className="col-span-12 xl:col-span-5 rounded-2xl border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200/70 bg-gradient-to-r from-violet-50 to-purple-50 px-4 py-3 rounded-t-2xl">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-violet-600" />
                    <h3 className="text-sm font-semibold text-slate-800">Reset Password</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="rounded-lg bg-white px-3 py-1.5 text-sm text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    {showPwd ? "Hide" : "Show"}
                  </button>
                </div>

                {showPwd && (
                  <div className="p-4 lg:p-6">
                    {/* New password */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={revealNew ? "text" : "password"}
                          value={pwd}
                          onChange={(e) => setPwd(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 bg-white pr-10 px-3 py-2 text-slate-900 shadow-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-200"
                        />
                        <button
                          type="button"
                          onClick={() => setRevealNew((s) => !s)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100"
                          title={revealNew ? "Hide" : "Show"}
                        >
                          {revealNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">At least 8 characters.</p>
                    </div>

                    {/* Confirm password */}
                    <div className="mt-4">
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={revealConfirm ? "text" : "password"}
                          value={pwd2}
                          onChange={(e) => setPwd2(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 bg-white pr-10 px-3 py-2 text-slate-900 shadow-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-200"
                        />
                        <button
                          type="button"
                          onClick={() => setRevealConfirm((s) => !s)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100"
                          title={revealConfirm ? "Hide" : "Show"}
                        >
                          {revealConfirm ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="mt-5 flex items-center justify-end gap-2">
                      <button
                        onClick={setPassword}
                        disabled={pwdSaving}
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-black focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
                      >
                        <KeyRound className="h-4 w-4" />
                        {pwdSaving ? "Updating..." : "Set Password"}
                      </button>
                    </div>

                    <p className="mt-3 text-xs text-slate-600">
                      Passwords are stored as <b>Argon2id</b> hashes in the database.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
